import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { AssessmentRowComponent } from '../assessment-row/assessment-row.component';
import { EmrInputComponent } from "../../../../shared/emr-input/emr-input.component";
import { EmrCheckboxComponent } from "../../../../shared/emr-checkbox/emr-checkbox.component";
import { IntakeOutputFormGroup } from 'src/app/calvin-emr-shared/models/assessments/intake-output.model';
import { Patient } from 'src/app/calvin-emr-shared/models/patient.model';
import { FormOption } from 'src/app/shared/models/form-option.model';

@Component({
  selector: 'app-intake-output-form',
  templateUrl: './intake-output-form.component.html',
  styleUrls: ['./intake-output-form.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AssessmentRowComponent,
    EmrInputComponent,
    EmrCheckboxComponent,
  ],
})
export class IntakeOutputFormComponent {
  formGroup = input.required<IntakeOutputFormGroup>();
  patient = input.required<Patient>();



  ngOnInit(): void {
    // Add listener to changes in the fall risk form, which updates
    //  fall score each time a change occurs
    this.formGroup().valueChanges.subscribe(() => {
      this.formGroup().controls.LSscore.setValue(this.computeLSScore(),
        { emitEvent: false });    // don't trigger the subscribe we are in!
    });
  }



  get isInfantNutrition(): boolean {
    return !!this.formGroup().controls.infantNutrition.value;
  }

  get isBreastfeeding(): boolean {
    return !!this.formGroup().controls.breastfeeding.value;
  }

  get isBottlefeeding(): boolean {
    return !!this.formGroup().controls.bottlefeeding.value;
  }


  LATCH_ZERO = "Too sleepy or reluctant; No achieved latch or such achieved";
  LATCH_ONE = "Repeated attempts for sustained latch or suck; Holds nipple in mouth; Stimulates to suck";
  LATCH_TWO = "Grasps breast; Tongue down; Lips flanged; Rhythmic sucking";
  latchOptions = [
    new FormOption(this.LATCH_ZERO),
    new FormOption(this.LATCH_ONE),
    new FormOption(this.LATCH_TWO),
  ];

  AUDIBLE_ZERO = "None";
  AUDIBLE_ONE = "A few with stimulation";
  AUDIBLE_TWO = "Spontaneous and intermittent (< 24 hours old); Spontaneous and frequent (> 24 hours old)";
  audibleOptions = [
    new FormOption(this.AUDIBLE_ZERO),
    new FormOption(this.AUDIBLE_ONE),
    new FormOption(this.AUDIBLE_TWO),
  ];

  TYPE_ZERO = "Inverted";
  TYPE_ONE = "Flat";
  TYPE_TWO = "Everted (After stimulation)";
  typeOptions = [
    new FormOption(this.TYPE_ZERO),
    new FormOption(this.TYPE_ONE),
    new FormOption(this.TYPE_TWO),
  ];

  COMFORT_ZERO = "Engorged; Cracked, bleeding, large blisters or bruises; Severe discomfort";
  COMFORT_ONE = "Filling; Reddened/small blisters or bruises";
  COMFORT_TWO = "Soft; Non-tender";
  comfortOptions = [
    new FormOption(this.COMFORT_ZERO),
    new FormOption(this.COMFORT_ONE),
    new FormOption(this.COMFORT_TWO),
  ];

  HOLD_ZERO = "Full assist (Staff holds infant at breast)";
  HOLD_ONE = "Minimal assist (Mother positions infant at breast)";
  HOLD_TWO = "No assist from staff; Mother able to position infant at breast";
  holdOptions = [
    new FormOption(this.HOLD_ZERO),
    new FormOption(this.HOLD_ONE),
    new FormOption(this.HOLD_TWO),
  ];

  computeLSLatchScore(): number {
    const latch = this.formGroup().controls.LSlatch.value;
    switch (latch) {
      case this.LATCH_ZERO:
        return 0;
      case this.LATCH_ONE:
        return 1;
      case this.LATCH_TWO:
        return 2;
      default:
        return 0;
    }
  }

  computeLSAudibleScore(): number {
    const audible = this.formGroup().controls.LSaudible.value;
    switch (audible) {
      case this.AUDIBLE_ZERO:
        return 0;
      case this.AUDIBLE_ONE:
        return 1;
      case this.AUDIBLE_TWO:
        return 2;
      default:
        return 0;
    }
  }

  computeLSTypeScore(): number {
    const type = this.formGroup().controls.LStype.value;
    switch (type) {
      case this.TYPE_ZERO:
        return 0;
      case this.TYPE_ONE:
        return 1;
      case this.TYPE_TWO:
        return 2;
      default:
        return 0;
    }
  }

  computeLSComfortScore(): number {
    const comfort = this.formGroup().controls.LScomfort.value;
    switch (comfort) {
      case this.COMFORT_ZERO:
        return 0;
      case this.COMFORT_ONE:
        return 1;
      case this.COMFORT_TWO:
        return 2;
      default:
        return 0;
    }
  }

  computeLSHoldScore(): number {
    const hold = this.formGroup().controls.LShold.value;
    switch (hold) {
      case this.HOLD_ZERO:
        return 0;
      case this.HOLD_ONE:
        return 1;
      case this.HOLD_TWO:
        return 2;
      default:
        return 0;
    }
  }

  computeLSScore() {
    const totalScore =
      this.computeLSLatchScore() +
      this.computeLSAudibleScore() +
      this.computeLSTypeScore() +
      this.computeLSComfortScore() +
      this.computeLSHoldScore();
    return totalScore;
  }
}